import styled, { keyframes } from "styled-components";
import { mobileAuthSideContainer, mobileAuthMainContainer } from "../../../styles/authShellMixins";

// Animações
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Container principal
export const MainContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  min-height: 100vh;
  height: 100%;
  background: var(--app-bg-gradient);
  background-size: 400% 400%;
  font-family: "Inter", "Helvetica Neue", sans-serif;
  overflow-x: hidden;

  ${mobileAuthMainContainer}
`;

export const SideContainer = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  position: relative;
  height: 100vh;
  min-height: 100vh;
  width: ${({ collapsed }) => (collapsed ? "calc(100% - 6vw)" : "calc(100% - 15vw)")};
  margin: 0;
  transition: margin-left 0.5s ease-in-out, width 0.5s ease-in-out;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: var(--scrollbar-track, transparent);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--accent, #6b8f71);
    border-radius: 10px;
  }

  ${mobileAuthSideContainer}
`;

export const PageContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: none;
  box-sizing: border-box;
  padding: 0.75rem 1.25rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  flex: 0 0 auto;
  height: auto;
  min-height: min-content;
  overflow: visible;
`;

// Container das tabelas — cresce com o conteúdo; scroll na página (SideContainer)
export const TableContainer = styled.div`
  width: 100%;
  max-width: none;
  flex: 0 0 auto;
  height: auto;
  min-height: min-content;
  max-height: none;
  overflow-x: auto;
  overflow-y: visible;
  margin: 0.5rem 0 0;
  align-self: stretch;
  background: var(--surface-glass);
  backdrop-filter: blur(20px);
  border: 1px solid var(--accent-secondary-soft);
  border-radius: 16px;
  padding: 1rem 1rem 1.25rem;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--surface-overlay);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, var(--accent) 0%, var(--accent-secondary) 100%);
  }

  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

export const EmptyMessage = styled.div`
  padding: 1.25rem 0.5rem;
  color: ${({ $error }) => ($error ? "var(--danger)" : "var(--text-secondary)")};
  font-size: 0.95rem;
  line-height: 1.5;
  text-align: left;
`;


// Linha
export const Row = styled.div`
  width: 90%;
  max-width: 1200px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  gap: 2rem;
  margin: 1rem auto;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 95%;
    gap: 1rem;
  }
`;

// Coluna esquerda
export const LeftColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 48%;
  min-width: 200px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

// Coluna direita
export const RightColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 48%;
  min-width: 200px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;


export const HeaderSection = styled.header`
  text-align: left;
  width: 100%;
  margin-bottom: 0.25rem;
`;

export const Title = styled.h2`
  font-size: 1.65rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0.5rem 0 0.35rem 0;
  text-shadow: 0 4px 20px color-mix(in srgb, var(--accent-secondary) 30%, transparent);
  text-align: left;
  animation: ${fadeInUp} 0.6s ease-out;
  animation-delay: 0.1s;
  animation-fill-mode: both;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.35rem;
  }
`;

export const Subtitle = styled.p`
  font-size: 0.95rem;
  font-weight: 400;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.45;
  text-align: left;
  max-width: 42rem;
`;

export const LoadingContainer = styled.div`
  width: 100%;
  min-height: 40vh;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
`;

export const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-subtle);
  border-top-color: var(--accent-secondary);
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;